import React, { useEffect, useState } from 'react';

import { Box, Button, Group, Stack } from '@mantine/core';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useQueryClient } from 'react-query';
import notificationHandler from 'utils/notifications';

import { IAutoPayOnAttach } from '../types';

interface IStripePaymentMethodFormProps {
  athleteId: string;
  athleteEmail: string;
  athleteFullName: string;
  athletePhone: string;
  setAsDefault: boolean;
  /** Set only when the staff auto-pay toggle is on offer; undefined leaves the flow as it was. */
  autoPay?: IAutoPayOnAttach;
  handleCancel: () => void;
  handleSuccess: (stripePaymentMethodId: string) => void;
}

const StripePaymentMethodForm = ({
  athleteId,
  athleteEmail,
  athleteFullName,
  athletePhone,
  setAsDefault,
  autoPay,
  handleCancel,
  handleSuccess,
}: IStripePaymentMethodFormProps): JSX.Element => {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const athleteTracking = useAthleteTracking();
  const { trackPaymentMethodAutoPaySelected } = useStaffTracking();
  const { successNotification, errorNotification } = notificationHandler();

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  let timer: NodeJS.Timeout;

  const submitPayment = async (): Promise<void> => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setLoadingSubmit(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/users/athletes/${athleteId}`,
        payment_method_data: {
          // The add-card flow never creates the payment method through a Kilo endpoint: Stripe
          // does, and the webhook reads these back. The auto-pay flag rides the same channel the
          // set-as-default checkbox already uses, and is sent only when the toggle was offered.
          metadata: {
            set_as_default: setAsDefault ? 'true' : 'false',
            ...(autoPay ? { enable_auto_pay: autoPay.enabled ? 'true' : 'false' } : {}),
          },
        },
      },
      redirect: 'if_required',
    });

    setLoadingSubmit(false);
    if (error) {
      errorNotification('Payment method creation has failed', `${error.message}.`);
    } else {
      successNotification(
        'Payment method creation success',
        'Your payment method is being processed, please wait.',
      );

      athleteTracking.trackAddPaymentMethod('stripe');
      if (autoPay) {
        trackPaymentMethodAutoPaySelected(athleteId, autoPay.packageCount, autoPay.enabled);
      }

      handleSuccess(String(setupIntent?.payment_method) || '');
    }

    timer = setTimeout(() => {
      queryClient.invalidateQueries(['paymentMethods']);
      queryClient.invalidateQueries(['paymentMethodsSetupIntent']);
      if (autoPay?.enabled) {
        // The same webhook that attaches the card enables auto-pay on the member's packages and
        // their upcoming invoices, so both read models are stale once it lands. Leaving them
        // would render the old value straight after staff changed it.
        queryClient.invalidateQueries(['athletePackages']);
        queryClient.invalidateQueries(['invoices']);
      }
    }, 5000);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timer);
    };
  });

  return (
    <Stack>
      <Box px='xl'>
        <PaymentElement
          options={{
            defaultValues: {
              billingDetails: {
                email: athleteEmail,
                name: athleteFullName,
                phone: athletePhone,
              },
            },
          }}
        />
      </Box>
      <Group position='right' mt='xl'>
        <Button variant='outline' disabled={loadingSubmit} type='button' onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          onClick={submitPayment}
          disabled={!stripe || !elements || loadingSubmit}
          loading={loadingSubmit}
        >
          Save
        </Button>
      </Group>
    </Stack>
  );
};
export default StripePaymentMethodForm;
